import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Download, UploadFile } from '@mui/icons-material';

import { ILegalRequirement } from '../../Interfaces/ILegalRequirement';
import { IDocument } from '../../Interfaces/IDocument';
import { ILegalPhaseFullObject } from '../../Interfaces/ILegalPhase';
import { LegalRequirementDocumentDialog } from './LegalRequirementDocumentDialog';

interface IProps {
  legalPhaseFullObjectArray: ILegalPhaseFullObject[];
  setLegalPhaseFullObjectArray: Function;
}

export const LegalPhaseDocumentView: React.FC<IProps> = ({
                                                           legalPhaseFullObjectArray,
                                                           setLegalPhaseFullObjectArray,
                                                         }) => {
  const [currentRequirements, setCurrentRequirements] = useState<
    ILegalRequirement[]
  >([]);
  const [currentReq, setCurrentReq] = useState<ILegalRequirement | null>(null);

  const [isOpenClient, setIsOpenClient] = useState(false);
  const [isOpenEmployees, setIsOpenEmployees] = useState(false);
  const [documentArrayClient, setDocumentArrayClient] = useState<IDocument[]>(
    []
  );
  const [documentArrayEmployees, setDocumentArrayEmployees] = useState<
    IDocument[]
  >([]);
  const [isSendEmailPossible, setIsSendEmailPossible] =
    useState<boolean>(false);

  const handleOpenEmployees = (localReq: ILegalRequirement) => {
    setIsOpenEmployees(true);
    setCurrentReq(localReq);
    setDocumentArrayEmployees(localReq.DocumentArrayEmployees ?? []);
    setIsSendEmailPossible(
      localReq.sendMail || localReq.isInterneLegalRequirement
    );
  };

  const handleOpenClient = (localReq: ILegalRequirement) => {
    setIsOpenClient(true);
    setCurrentReq(localReq);
    setDocumentArrayClient(localReq.DocumentArrayClient ?? []);
  };

  const handleSave = () => {
    if (!currentReq) return;

    let updatedReq: ILegalRequirement = { ...currentReq };

    if (isOpenClient) {
      updatedReq = {
        ...updatedReq,
        DocumentArrayClient: documentArrayClient,
      };
    }

    if (isOpenEmployees) {
      updatedReq = {
        ...updatedReq,
        DocumentArrayEmployees: documentArrayEmployees,
      };
    }

    const updatedPhases = legalPhaseFullObjectArray.map(phase => ({
      ...phase,
      LegalRequirementArray: phase.LegalRequirementArray.map(req =>
        req.idLegalRequirement === updatedReq.idLegalRequirement
          ? updatedReq
          : req
      ),
    }));

    setLegalPhaseFullObjectArray(updatedPhases);

    setCurrentReq(null);
    setIsOpenClient(false);
    setIsOpenEmployees(false);
  };

  useEffect(() => {
    const tmpArray: ILegalRequirement[] = [];

    legalPhaseFullObjectArray.forEach(currentPhase =>
      currentPhase.LegalRequirementArray.forEach(currentReq => {
        if (
          Boolean(currentReq.canUploadFilesClient) ||
          Boolean(currentReq.canUploadFilesEmployees)
        ) {
          tmpArray.push(currentReq);
        }
      })
    );

    setCurrentRequirements(tmpArray);
  }, [legalPhaseFullObjectArray]);

  const renderDocumentList = (
    documents: IDocument[] | undefined,
    canUpload: boolean
  ) => {
    if (!documents || documents.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" component="span">
          {canUpload ? 'Keine Dateien vorhanden' : 'Datenaustausch deaktiviert'}
        </Typography>
      );
    }

    return (
      <Box
        display="flex"
        flexWrap="wrap"
        gap={0.5}
      >
        {documents.map((document, index) => (
          <Chip
            key={`${document.FileName}-${index}`}
            label={document.FileName}
            size="small"
            variant="outlined"
          />
        ))}
      </Box>
    );
  };

  return (
    <>
      {currentReq && (
        <>
          <LegalRequirementDocumentDialog
            open={isOpenClient}
            onClose={() => setIsOpenClient(false)}
            title="Bereitgestellte Daten des Mandanten"
            allowUpload={false}
            allowRemove={true}
            documentArray={documentArrayClient}
            setDocumentArray={setDocumentArrayClient}
            legalRequirementObject={currentReq}
            handleSave={handleSave}
          />
          <LegalRequirementDocumentDialog
            open={isOpenEmployees}
            onClose={() => setIsOpenEmployees(false)}
            title="Bereitgestellte Daten für den Mandanten"
            allowUpload={true}
            allowRemove={true}
            documentArray={documentArrayEmployees}
            setDocumentArray={setDocumentArrayEmployees}
            legalRequirementObject={currentReq}
            handleSave={handleSave}
            isStandarddocument
            isSendEmailPossible={isSendEmailPossible}
          />
        </>
      )}

      <Typography variant="h6" gutterBottom>
        Datenaustausch zu Maßnahmen
      </Typography>

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow
              sx={theme => ({
                '& th': {
                  backgroundColor: theme.palette.action.hover,
                  fontWeight: 600,
                },
              })}
            >
              <TableCell width="20%">
                Maßnahme
              </TableCell>
              <TableCell width="40%">
                Bereitgestellte Daten des Mandanten
              </TableCell>
              <TableCell width="40%">
                Bereitgestellte Daten für den Mandanten
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentRequirements.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" fontStyle="italic">
                    Derzeitig sind keine Einträge verfügbar.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {currentRequirements.map((requirement, index) => (
              <TableRow
                key={`currentReq-documentview-${requirement.idLegalRequirement}`}
                sx={theme => ({
                  backgroundColor:
                    index % 2 === 0
                      ? 'inherit'
                      : 'rgba(0, 0, 0, 0.03)',
                })}
              >
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <Typography variant="body2">
                    {requirement.LegalRequirement}
                  </Typography>
                </TableCell>

                {/* Mandanten */}
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box flex={1}>
                      {renderDocumentList(
                        requirement.DocumentArrayClient,
                        !!requirement.canUploadFilesClient
                      )}
                    </Box>
                    {requirement.canUploadFilesClient &&
                      requirement.DocumentArrayClient &&
                      requirement.DocumentArrayClient.length > 0 && (
                        <Tooltip title="Herunterladen von bereitgestellten Daten">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenClient(requirement)}
                            aria-label="Mandantendaten verwalten"
                          >
                            <Download fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                  </Stack>
                </TableCell>

                {/* Mitarbeiter */}
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box flex={1}>
                      {renderDocumentList(
                        requirement.DocumentArrayEmployees,
                        !!requirement.canUploadFilesEmployees
                      )}
                    </Box>
                    {requirement.canUploadFilesEmployees && (
                      <Tooltip title="Daten für den Mandanten hochladen">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEmployees(requirement)}
                          aria-label="Daten für Mandanten verwalten"
                        >
                          <UploadFile fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
